import { Link, NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuthStore } from '@/stores/auth';
import {
  LayoutDashboard,
  Film,
  Settings,
  CreditCard,
  LogOut,
  Sparkles,
  User,
  X,
} from 'lucide-react';
import { clsx } from 'clsx';

const navItems = [
  { to: '/', label: 'ホーム', icon: LayoutDashboard },
  { to: '/editor/new', label: '作成', icon: Film, primary: true },
  { to: '/billing', label: 'ライセンス', icon: CreditCard },
  { to: '/settings', label: '設定', icon: Settings },
];

export function Layout() {
  const { email, logout, isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const authed = isAuthenticated();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!authed) navigate('/signin', { replace: true });
  }, [authed, navigate]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  if (!authed) return null;

  // Hide bottom nav inside the Editor to maximize canvas area
  const isEditor = location.pathname.startsWith('/editor/');

  return (
    <div className="flex min-h-full flex-col">
      {/* Top bar */}
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-800 bg-slate-950/90 px-4 py-3 backdrop-blur">
        <Link to="/" className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-brand-500" />
          <span className="font-semibold tracking-tight text-sm">AI動画生成</span>
        </Link>
        <button
          onClick={() => setMenuOpen(true)}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-800 active:bg-slate-700"
          aria-label="アカウントメニュー"
        >
          <User className="h-4 w-4" />
        </button>
      </header>

      {/* Main */}
      <main
        className={clsx(
          'flex-1 overflow-auto',
          !isEditor && 'pb-[68px]', // room for bottom nav
        )}
      >
        <Outlet />
      </main>

      {/* Bottom nav */}
      {!isEditor && (
        <nav
          className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-800 bg-slate-950/95 backdrop-blur pb-safe"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <div className="mx-auto flex max-w-xl items-stretch justify-around px-2 py-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  clsx(
                    'flex flex-1 flex-col items-center gap-0.5 rounded-lg px-2 py-2 text-[10px] transition min-h-[56px]',
                    isActive
                      ? 'text-brand-400'
                      : 'text-slate-400 active:bg-slate-800',
                    item.primary && 'relative',
                  )
                }
              >
                {item.primary ? (
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-500 text-white shadow-lg shadow-brand-500/30">
                    <item.icon className="h-5 w-5" />
                  </span>
                ) : (
                  <item.icon className="h-5 w-5" />
                )}
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>
        </nav>
      )}

      {/* Account sheet */}
      {menuOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
            onClick={() => setMenuOpen(false)}
          />
          <div
            className="fixed inset-x-0 bottom-0 z-50 rounded-t-2xl border-t border-slate-800 bg-slate-900 p-4 pb-[calc(env(safe-area-inset-bottom)+1rem)]"
            role="dialog"
          >
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-slate-700" />
            <div className="flex items-center justify-between mb-4">
              <div className="min-w-0">
                <div className="text-xs text-slate-500">ログイン中</div>
                <div className="text-sm truncate">{email}</div>
              </div>
              <button
                onClick={() => setMenuOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-800"
                aria-label="閉じる"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <button
              onClick={() => {
                logout();
                navigate('/signin');
              }}
              className="btn-secondary w-full"
            >
              <LogOut className="h-4 w-4" />
              ログアウト
            </button>
          </div>
        </>
      )}
    </div>
  );
}
